import { forwardRef, useCallback, useEffect, useImperativeHandle, useRef, useState } from 'react';
import {
  batchUpdatePermissionGroups,
  fetchPermissionGroupList,
  fetchPermissionList,
  selectIsAdminUser,
  selectPermissionGroups,
  selectPermissions,
} from '@icp/form-renderer-core';
import { message, restApi } from '@icp/settings';
import PropTypes from 'prop-types';
import { useAppContext } from '@icp/app';
import { useTranslation } from 'react-i18next';
import clsx from 'clsx';
import { Divider, Modal } from 'antd';
import { Loading } from '@icp/components';
import { chooseFile } from '@icp/utils';
import { useClassName } from '../../hooks';
import { useElementDecorator, useIsInDesign } from '../../FormRenderCtx';
import { ConditionalPropertyPropType } from '../../propTypes';
import { useDispatch, useSelector } from '../../store';
import PermissionGroupsBatchConfirmDialog from './PermissionGroupsBatchConfirmDialog';
import PermissionGroupsBatchEditTable from './PermissionGroupsBatchEditTable';
import { withFieldWrapper } from '../../fieldWrapper';
import './PermissionGroupsBatchEditorElement.css';
import { ButtonUI } from '../ButtonElement';

const PermissionGroupsBatchEditorElement = forwardRef((props, ref) => {
  const {
    keyPath,
    id,
    className: classNameProp,
    style,
    componentProps = {},
    readonly,
    disabled,
  } = props;
  const { style: compStyle, className: compClassNameProp } = componentProps;
  const className = useClassName(classNameProp);
  const classNameComp = useClassName(compClassNameProp);
  const ElementDecorator = useElementDecorator();
  const isInDesign = useIsInDesign();

  const nodeRef = useRef(null);
  useImperativeHandle(
    ref,
    () => ({
      node: nodeRef.current,
    }),
    [],
  );

  const appCtx = useAppContext();
  const { projectId } = isInDesign ? {} : appCtx?.pbc || {};

  const { t } = useTranslation(['icp-form-renderer', 'icp-common']);
  const [batchChangedData, setBatchChangedData] = useState(null);
  const [openBatchConfirmModal, setOpenBatchConfirmModal] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [refresh, setRefresh] = useState(false);
  const [modal, contextHolder] = Modal.useModal();
  const tableRef = useRef(null);

  const dispatch = useDispatch();
  const isLoading = useSelector(
    (state) => state.permission.isLoading || state.permission.isLoadingOfPermissionGroups,
  );
  const error = useSelector((state) => state.permission.errorOfPermissionGroups);
  const permissions = useSelector(selectPermissions);
  const permissionGroups = useSelector(selectPermissionGroups);
  const isAdminUser = useSelector(selectIsAdminUser);
  const isReadOnly = !isAdminUser || readonly || disabled || isLoading;

  useEffect(() => {
    if (isInDesign) {
      return () => {};
    }
    const p1 = dispatch(fetchPermissionList());
    const p2 = dispatch(fetchPermissionGroupList({ projectId }));
    return () => {
      p1?.abort();
      p2?.abort();
    };
  }, [dispatch, isInDesign, projectId, refresh]);

  useEffect(() => {
    if (error) {
      message.error(t('error-occurred', { ns: 'icp-common' }));
      console.error(error);
    }
  }, [error, t]);

  const handleConfirmSaveForBatchModification = useCallback(() => {
    const { modifiedGroups } = batchChangedData || {};
    setIsSubmitting(true);
    dispatch(batchUpdatePermissionGroups({ projectId, permissionGroups: modifiedGroups }))
      .unwrap()
      .then(() => {
        const groupNames = modifiedGroups.map((group) => group.name).join(', ');
        message.success(t('permission-groups-batch-editor.messages.updated', { groupNames }));
        setOpenBatchConfirmModal(false);
        setBatchChangedData(null);
      })
      .finally(() => setIsSubmitting(false));
  }, [batchChangedData, dispatch, projectId, t]);

  const handleClickSaveForBatchModification = useCallback(() => {
    const changedData = tableRef.current?.getChangedData();
    if (changedData && changedData.differences.length > 0) {
      setBatchChangedData(changedData);
      setOpenBatchConfirmModal(true);
    } else {
      message.info(t('permission-groups-batch-editor.messages.no-changes'));
    }
  }, [t]);

  const handleImport = () => {
    modal.confirm({
      title: t('permission-groups-batch-editor.import-confirm.title'),
      content: t('permission-groups-batch-editor.import-confirm.content'),
      onOk: () => {
        chooseFile({ accept: 'application/json' }).then((file) => {
          const formData = new FormData();
          formData.set('file', file);
          return restApi
            .post('/form/api/user-management/permission-group/import-with-json-file', formData)
            .then(() => {
              message.success(t('permission-groups-batch-editor.messages.import-successful'));
              setRefresh(!refresh);
            })
            .catch((e) => {
              message.error(t('permission-groups-batch-editor.messages.import-failed'));
              console.error(e);
            });
        });
      },
    });
  };

  const handleExport = () => {
    restApi
      .get('/form/api/user-management/permission-group/export-with-json-file', {
        responseType: 'blob',
      })
      .then((response) => {
        try {
          const blob = new Blob([response], { type: 'application/json' });
          const url = URL.createObjectURL(blob);
          const link = document.createElement('a');
          link.href = url;
          link.download = 'permission_groups.json';
          document.body.appendChild(link);
          link.click();
          link.remove();
          window.URL.revokeObjectURL(url);
        } catch (e) {
          console.error(e);
        }
      });
  };

  return (
    <ElementDecorator keyPath={keyPath} id={id}>
      <div
        className={clsx('permission-groups-batch-editor-element', className, classNameComp)}
        style={style}
        ref={nodeRef}
      >
        <div className="permission-groups-batch-editor-header">
          <span className="permission-groups-batch-editor-header-labels">
            {t('permission-groups-batch-editor.hint')}
          </span>
          <div className="permission-groups-batch-editor-header-actions">
            <ButtonUI
              icon="material:file-upload-rounded"
              disabled={isInDesign}
              onClick={handleExport}
            >
              {t('export', { ns: 'icp-common' })}
            </ButtonUI>
            <ButtonUI
              icon="material:file-download-rounded"
              disabled={isReadOnly || isInDesign}
              onClick={handleImport}
            >
              {t('import', { ns: 'icp-common' })}
            </ButtonUI>
            <Divider type="vertical" />
            <ButtonUI
              type="primary"
              disabled={isReadOnly || isInDesign}
              onClick={handleClickSaveForBatchModification}
            >
              {t('save', { ns: 'icp-common' })}
            </ButtonUI>
          </div>
        </div>
        {openBatchConfirmModal ? (
          <PermissionGroupsBatchConfirmDialog
            changedData={batchChangedData}
            isLoading={isLoading}
            onOk={handleConfirmSaveForBatchModification}
            onCancel={() => setOpenBatchConfirmModal(false)}
          />
        ) : null}
        {isLoading && !isSubmitting ? (
          <Loading />
        ) : (
          <PermissionGroupsBatchEditTable
            ref={tableRef}
            projectId={projectId}
            permissions={permissions}
            permissionGroups={permissionGroups}
            readonly={isReadOnly}
            style={compStyle}
          />
        )}
      </div>
      {contextHolder}
    </ElementDecorator>
  );
});

PermissionGroupsBatchEditorElement.propTypes = {
  keyPath: PropTypes.string,
  id: PropTypes.string,
  className: PropTypes.string,
  style: PropTypes.shape({}),
  componentProps: PropTypes.shape({
    className: PropTypes.string,
    style: PropTypes.shape({}),
  }),
  disabled: ConditionalPropertyPropType(PropTypes.bool),
  readonly: ConditionalPropertyPropType(PropTypes.bool),
};

PermissionGroupsBatchEditorElement.displayName = 'PermissionGroupsBatchEditor';

export default withFieldWrapper(PermissionGroupsBatchEditorElement);
